import { useMemo, useState } from 'react'
import {
  exportAudience,
  importProspects,
  AD_PLATFORM_LABEL,
  type AdPlatform,
  type AudienceSummary,
  type RemoteProspectCampaign
} from '../../lib/adminClient'
import { num } from '../../lib/format'

// The ad-platform match lists and importing a research batch, moved here
// unchanged from the first Prospects page. No platform gives a per-person id;
// see AudiencesTab's own explanation.

const selectClass = 'rounded-md bg-white/[0.03] border border-white/10 px-2 py-1 text-xs text-slate-200'

export function ImportPanel({ onImported }: { onImported: () => void }): JSX.Element {
  const [text, setText] = useState('')
  const [batch, setBatch] = useState(new Date().toISOString().slice(0, 10))
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const parsed = useMemo(() => {
    if (!text.trim()) return { items: [] as Array<Record<string, unknown>>, problem: null as string | null }
    try {
      const raw: unknown = JSON.parse(text)
      if (!Array.isArray(raw)) return { items: [], problem: 'That is JSON, but not a list of people.' }
      // The researcher's own key names, mapped to the server's. Anything not
      // listed is ignored rather than guessed at.
      const items = raw.map((r) => {
        const o = r as Record<string, unknown>
        const pick = (...keys: string[]): unknown => keys.map((k) => o[k]).find((v) => v !== undefined && v !== '')
        return {
          name: pick('name'),
          linkedinUrl: pick('url', 'linkedin_url', 'linkedinUrl'),
          urlSource: pick('url_source', 'urlSource'),
          email: pick('email'),
          role: pick('role', 'headline_role'),
          company: pick('company'),
          location: pick('location'),
          country: pick('country'),
          segment: pick('segment'),
          subNiche: pick('sub_niche', 'subNiche'),
          contentFocus: pick('content_focus', 'contentFocus'),
          followers: pick('followers', 'follower_estimate'),
          followerSource: pick('follower_source', 'followerSource'),
          fitScore: pick('fit', 'fit_score', 'fitScore'),
          responsivenessScore: pick('resp', 'responsiveness_score', 'responsivenessScore'),
          confidence: pick('confidence'),
          evidenceType: pick('evidence', 'evidence_type'),
          activityEvidence: pick('activity', 'activity_evidence'),
          responsivenessSignals: pick('resp_signals', 'responsiveness_signals'),
          whyPlexii: pick('fit_why', 'plexii_fit', 'whyPlexii'),
          hook: pick('hook', 'personalised_hook'),
          otherChannels: pick('other_channels', 'otherChannels'),
          watchOuts: pick('conflicts', 'watch_outs', 'watchOuts'),
          factCheck: typeof o.verdict === 'string' ? o.verdict.toLowerCase() : undefined,
          factCheckNote: pick('what_changed', 'factCheckNote'),
          draftNote: pick('note', 'draftNote'),
          draftDm: pick('dm', 'draftDm'),
          externalId: pick('external_id', 'externalId', 'airtable_id')
        }
      })
      const nameless = items.filter((i) => !i.name).length
      return { items, problem: nameless ? `${nameless} of them have no name and will be refused.` : null }
    } catch {
      return { items: [], problem: 'That is not JSON.' }
    }
  }, [text])

  const unverifiable = parsed.items.filter((i) => i.factCheck === 'unverifiable').length
  const unchecked = parsed.items.filter((i) => !i.factCheck).length

  async function run(): Promise<void> {
    setBusy(true)
    setError(null)
    const res = await importProspects(parsed.items, 'research run', batch)
    setBusy(false)
    if (!res.ok) {
      setError(res.error)
      return
    }
    const { imported, duplicates, errors } = res.data
    setResult(
      `Imported ${imported}.${duplicates ? ` ${duplicates} already here, skipped.` : ''}${errors.length ? ` ${errors.length} refused.` : ''}`
    )
    if (imported) {
      setText('')
      onImported()
    }
  }

  return (
    <div className="glass-card rounded-xl p-4 mb-4" data-testid="prospects-import">
      <div className="text-sm text-slate-300 mb-2">
        Paste the research run&rsquo;s JSON. Anyone already stored is reported as a duplicate rather than added twice, so pasting the
        same file again is safe.
      </div>
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setResult(null)
        }}
        rows={7}
        placeholder='[{"name": "…", "url": "https://www.linkedin.com/in/…", "hook": "…", "note": "…", "dm": "…", "verdict": "PASS"}]'
        data-testid="prospects-import-text"
        className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:border-accent"
      />
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <label className="text-xs text-slate-400">
          Batch
          <input value={batch} onChange={(e) => setBatch(e.target.value)} data-testid="prospects-import-batch" className="ml-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-xs" />
        </label>
        <button className="btn-primary" disabled={busy || parsed.items.length === 0} onClick={() => void run()} data-testid="prospects-import-run">
          {busy ? 'Importing…' : `Import ${parsed.items.length} ${parsed.items.length === 1 ? 'person' : 'people'}`}
        </button>
        {result && (
          <span className="text-xs text-emerald-300" data-testid="prospects-import-result">
            {result}
          </span>
        )}
        {error && <span className="text-xs text-red-300">Import failed: {error}</span>}
      </div>
      {(parsed.problem || unverifiable > 0 || unchecked > 0) && (
        <div className="text-[11px] mt-2 space-y-0.5" data-testid="prospects-import-warnings">
          {parsed.problem && <div className="text-amber-200">{parsed.problem}</div>}
          {unverifiable > 0 && <div className="text-amber-200">{unverifiable} could not be verified. They come in flagged, and cannot be contacted until someone confirms them.</div>}
          {unchecked > 0 && <div className="text-slate-400">{unchecked} carry no fact-check verdict. They come in marked &ldquo;not checked&rdquo;, which is not the same as passed.</div>}
        </div>
      )}
    </div>
  )
}

export function AudiencesTab({ summary, campaigns }: { summary: AudienceSummary[]; campaigns: RemoteProspectCampaign[] }): JSX.Element {
  const [campaignId, setCampaignId] = useState('')
  const [busy, setBusy] = useState<AdPlatform | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  async function download(platform: AdPlatform): Promise<void> {
    setBusy(platform)
    setError(null)
    setNote(null)
    const res = await exportAudience(platform, campaignId || undefined)
    setBusy(null)
    if (!res.ok) {
      setError(res.error)
      return
    }
    const { header, rows, skippedNoEmail } = res.data
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\r\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `plexii-${platform}-match-list-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setNote(
      `${rows.length} ${rows.length === 1 ? 'hash' : 'hashes'} in the file.` +
        (skippedNoEmail ? ` ${skippedNoEmail} left out because we hold no address for them.` : '')
    )
  }

  return (
    <div>
      <div className="glass-card rounded-xl p-4 mb-4 text-sm text-slate-300">
        <div className="font-medium text-slate-100 mb-1">What this can and cannot do</div>
        <p className="max-w-3xl text-slate-400 text-[13px] leading-relaxed">
          None of these platforms will tell you a person&rsquo;s user id. Each one takes a list of hashed email addresses and reports
          how much of the list it matched to its own users, as a figure over the whole list. So what you can do here is export the
          list, upload it yourself, and record what came back. There are no ad-platform credentials on the server and nothing
          uploads by itself.
        </p>
        <p className="max-w-3xl text-slate-400 text-[13px] leading-relaxed mt-2">
          The one genuine per-person identifier is a click id, and it exists only when that person clicked one of our ads and then
          sent a form on the website. Those are counted below as real clicks, and shown on the person.
        </p>
        <div className="mt-3">
          <label className="text-xs text-slate-400">
            Export
            <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} data-testid="audience-campaign" className={`${selectClass} ml-2`}>
              <option value="">everyone</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  just {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error && <div className="text-xs text-red-300 mt-2">{error}</div>}
        {note && (
          <div className="text-xs text-emerald-300 mt-2" data-testid="audience-export-result">
            {note}
          </div>
        )}
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Platform</th>
              <th>Can be matched</th>
              <th>No address</th>
              <th>Uploaded</th>
              <th>Matched</th>
              <th>Real ad clicks</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {summary.map((s) => (
              <tr key={s.platform} data-testid="audience-row">
                <td>
                  <div className="text-slate-200">{AD_PLATFORM_LABEL[s.platform]}</div>
                  <div className="text-[10px] text-slate-500 max-w-[18rem]">{s.uploadsVia}</div>
                </td>
                <td className="tabular-nums text-slate-200">{num(s.matchable)}</td>
                <td className="tabular-nums text-slate-500">{num(s.unmatchable)}</td>
                <td className="tabular-nums text-slate-300">{num(s.byStatus.uploaded + s.byStatus.matched + s.byStatus.unmatched)}</td>
                <td className="tabular-nums text-emerald-300">{num(s.byStatus.matched)}</td>
                <td className="tabular-nums text-slate-300">{num(s.observedClicks)}</td>
                <td>
                  <button className="btn-ghost text-xs disabled:opacity-40" disabled={busy !== null || s.matchable === 0} onClick={() => void download(s.platform)} data-testid={`audience-export-${s.platform}`}>
                    {busy === s.platform ? 'Building…' : 'Export CSV'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-slate-500">
        &ldquo;Uploaded&rdquo; and &ldquo;Matched&rdquo; are what an operator recorded after uploading, not something a platform told this
        server. Nothing here is estimated.
      </div>
    </div>
  )
}

